import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { BusinessUnit } from '@/components/finance/BusinessUnitSelector';

export interface Expense {
  id: string;
  business_unit: string;
  brand: string | null;
  category_id: string | null;
  bank_account_id: string | null;
  description: string;
  supplier_name: string | null;
  supplier_contact_id: string | null;
  currency: string;
  amount: number;
  paid_amount: number | null;
  due_date: string;
  paid_at: string | null;
  status: string;
  recurrence: string | null;
  recurrence_day: number | null;
  recurrence_end_date: string | null;
  parent_expense_id: string | null;
  deal_id: string | null;
  created_by: string | null;
  approved_by: string | null;
  approved_at: string | null;
  notes: string | null;
  attachments: any;
  metadata: any;
  created_at: string | null;
  updated_at: string | null;
}

interface ExpenseFilters {
  businessUnit?: BusinessUnit;
  status?: string;
  categoryId?: string;
  isRecurring?: boolean;
  startDate?: string;
  endDate?: string;
  brand?: string;
  searchText?: string;
}

export function useExpenses(filters: ExpenseFilters = {}) {
  const { businessUnit = 'all', status, categoryId, isRecurring, startDate, endDate, brand, searchText } = filters;
  
  return useQuery({
    queryKey: ['expenses', filters],
    queryFn: async () => {
      let query = supabase
        .from('expenses')
        .select('*')
        .order('due_date', { ascending: true });
      
      if (businessUnit !== 'all') {
        query = query.eq('business_unit', businessUnit);
      }
      
      if (status) {
        query = query.eq('status', status);
      }
      
      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }
      
      if (isRecurring !== undefined) {
        if (isRecurring) {
          query = query.neq('recurrence', 'once');
        } else {
          query = query.eq('recurrence', 'once');
        }
      }
      
      if (startDate) {
        query = query.gte('due_date', startDate);
      }
      
      if (endDate) {
        query = query.lte('due_date', endDate);
      }
      
      if (brand) {
        query = query.eq('brand', brand);
      }
      
      if (searchText) {
        query = query.or(`description.ilike.%${searchText}%,supplier_name.ilike.%${searchText}%`);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as Expense[];
    },
  });
}

interface CreateExpenseData {
  business_unit: string;
  brand?: string;
  category_id?: string;
  bank_account_id?: string;
  description: string;
  supplier_name?: string;
  currency: string;
  amount: number;
  due_date: string;
  recurrence?: string;
  recurrence_day?: number;
  recurrence_end_date?: string;
  deal_id?: string;
  notes?: string;
  attachments?: string[];
}

export function useCreateExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: CreateExpenseData) => {
      const { data: result, error } = await supabase
        .from('expenses')
        .insert({
          ...data,
          status: 'pending',
        })
        .select()
        .single();
      
      if (error) throw error;
      return result as Expense;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
}

export function useUpdateExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<Expense> & { id: string }) => {
      const { data: result, error } = await supabase
        .from('expenses')
        .update(data)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return result as Expense;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
}

export function usePayExpense() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      id, 
      paid_amount, 
      paid_at,
      bank_account_id,
    }: { 
      id: string; 
      paid_amount: number; 
      paid_at: string;
      bank_account_id: string;
    }) => {
      if (!bank_account_id) {
        throw new Error('Conta bancária obrigatória para registrar pagamento de despesa');
      }
      const { data: result, error } = await supabase
        .from('expenses')
        .update({
          paid_amount,
          paid_at,
          bank_account_id,
          status: 'paid',
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return result as Expense;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
  });
}
